<template>
  <div class="p-3 apperty-panel">

    <!-- ============ SELECTOR DE USUARIO DEL CRM ============ -->
    <div class="apperty-user">
      <div v-if="selectedUser" class="apperty-user__chip">
        <div class="apperty-user__info">
          <span class="apperty-user__name">{{ userLabel(selectedUser) }}</span>
          <span class="apperty-user__mail">{{ selectedUser.email }}</span>
        </div>
        <soft-button type="button" color="secondary" variant="outline" size="sm" @click="clearUser">
          Cambiar
        </soft-button>
      </div>

      <template v-else>
        <label class="form-label fw-semibold">Usuario del CRM</label>
        <soft-input
          v-model:value="userQuery"
          type="text"
          placeholder="Buscar por nombre o correo..."
          aria-label="Buscar usuario del CRM"
        />
        <div v-if="isSearchingUsers" class="text-muted small mt-1">
          <i class="fas fa-spinner fa-spin me-1"></i> Buscando...
        </div>
        <ul v-else-if="userResults.length" class="apperty-user__list mt-2">
          <li v-for="u in userResults" :key="u.id" @click="selectUser(u)">
            <span class="apperty-user__name">{{ userLabel(u) }}</span>
            <span class="apperty-user__mail">{{ u.email }}</span>
          </li>
        </ul>
        <p v-else-if="userSearched" class="text-muted small mt-2 mb-0">
          Sin resultados para "{{ userQuery }}".
        </p>
        <small v-else class="text-muted">
          Elige el usuario al que se le guarda el token y se le asignan los inmuebles importados.
        </small>
      </template>
    </div>

    <div v-if="!selectedUser" class="apperty-empty mt-3">
      <i class="fas fa-user-check me-2"></i>
      Selecciona primero un usuario para ver su integración con Apperty.
    </div>

    <apperty-integration
      v-else
      :key="selectedUser.id"
      :userId="selectedUser.id"
      :userEmail="selectedUser.email"
      class="mt-2 px-0"
    />
  </div>
</template>

<script>
import { ref, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import SoftInput from '@/components/SoftInput.vue';
import SoftButton from '@/components/SoftButton.vue';
import appertyIntegration from './appertyIntegration.vue';

export default {
  name: 'FormApperty',
  components: { SoftInput, SoftButton, appertyIntegration },
  setup() {
    const store = useStore();

    const selectedUser     = ref(null);
    const userQuery        = ref('');
    const userResults      = ref([]);
    const isSearchingUsers = ref(false);
    const userSearched     = ref(false);

    const userLabel = (u) =>
      u?.name || [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.email || '—';

    const searchUsers = async () => {
      const query = userQuery.value.trim();
      if (query.length < 3) {
        userResults.value = [];
        userSearched.value = false;
        return;
      }
      isSearchingUsers.value = true;
      try {
        const res = await store.dispatch('appLonjas/searchMainUsersAction', { query });
        userResults.value = res?.data || res || [];
        userSearched.value = true;
      } catch (err) {
        console.error(err);
        userResults.value = [];
      } finally {
        isSearchingUsers.value = false;
      }
    };

    let searchTimer = null;
    watch(userQuery, () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(searchUsers, 400);
    });

    const selectUser = (user) => {
      selectedUser.value = user;
      userResults.value = [];
      userQuery.value = '';
      userSearched.value = false;
    };

    const clearUser = () => { selectedUser.value = null; };

    onUnmounted(() => clearTimeout(searchTimer));

    return {
      selectedUser, userQuery, userResults, isSearchingUsers, userSearched,
      userLabel, selectUser, clearUser,
    };
  },
};
</script>

<style scoped>
.apperty-panel { font-size: 0.85rem; }

.apperty-user__chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 10px 14px;
}

.apperty-user__info { display: flex; flex-direction: column; min-width: 0; }
.apperty-user__name { font-size: 0.8rem; font-weight: 600; color: #1E293B; }
.apperty-user__mail { font-size: 0.72rem; color: #64748B; }

.apperty-user__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
}

.apperty-user__list li {
  display: flex;
  flex-direction: column;
  padding: 8px 14px;
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;
}

.apperty-user__list li:last-child { border-bottom: none; }
.apperty-user__list li:hover { background: #F1F5F9; }

.apperty-empty {
  background: #F8FAFC;
  border: 1px dashed #CBD5E1;
  border-radius: 12px;
  padding: 18px 16px;
  text-align: center;
  font-size: 0.8rem;
  color: #64748B;
}
</style>
